import React, { useState, useRef, useEffect, useCallback } from 'react';
import { mergePages, type ComponentContentSchema, type PageDataMap, type SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyAddressUrl, usePropertyField, useCollectionBinding, useVibesites } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import type { Floorplan } from '@vibesites/schema';
import mergedPagesData from '../vibesites-pages.json';
import { FLOORPLAN_TYPE_PAGES, matchesFloorplanType } from '../utils/floorplanTypes';
import { withClosedDays } from '../utils/officeHours';

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface VirtualTourEntry {
  virtual_tour_url?: string | null;
}

/**
 * Upstream feeds sometimes pass literal strings like "false", "null", "none"
 * instead of an empty value — treat those as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

const DAY_ORDER: Record<string, number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

function dayOrderIndex(day: string | null | undefined): number {
  if (!day) return 99;
  const key = day.trim().toUpperCase().slice(0, 3);
  return DAY_ORDER[key] ?? 99;
}

function formatHourTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

function formatHoursValue(openRaw: string | null | undefined, closeRaw: string | null | undefined): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment Only';
  if (!open && !close) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!open || !close) return 'Closed';
  return `${formatHourTime(open)} – ${formatHourTime(close)}`;
}

function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const upper = day.trim().toUpperCase();
  if (upper.length <= 3) return upper;
  return upper.slice(0, 3);
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface NavItem {
  alias: string;
  label: string;
  href: string;
}

/** Full inheritance chain + this site's overrides, so nav respects `visible: false`. */
const mergedPages = mergePages(mergedPagesData as unknown as PageDataMap);

interface NavEntry {
  key: string;
  label: string;
  /** Present for plain links; groups use `children` instead. */
  href?: string;
  children?: NavItem[];
  group?: NavGroupKey;
}

type NavGroupKey = 'floorplans' | 'gallery' | 'amenities' | 'map' | 'more';

/**
 * Grouped menu structure: top-level links first, then dropdown groups.
 * Pages hidden via `visible: false` drop out of their group; a group left
 * with a single page renders as a plain link, and an empty group is removed.
 */
const NAV_STRUCTURE: Array<{ alias: string } | { group: NavGroupKey; aliases: string[] }> = [
  { alias: 'home' },
  {
    group: 'floorplans',
    aliases: ['floor-plans', 'studios', '1-bedroom', '2-bedrooms', '3-bedrooms', 'townhomes'],
  },
  { group: 'gallery', aliases: ['gallery', 'virtual-tour'] },
  { group: 'amenities', aliases: ['amenities', 'pet-policy'] },
  { group: 'map', aliases: ['neighborhood', 'interactive-map', 'sister-properties'] },
  { group: 'more', aliases: ['faq', 'reviews', 'preferred-employer-program', 'resident-access'] },
  { alias: 'contact' },
];

const NAV_CHILD_LABELS: Record<string, string> = {
  'floor-plans': 'All Floor Plans',
  gallery: 'Photos',
};

const NAV_GROUP_DEFAULT_LABELS: Record<NavGroupKey, string> = {
  floorplans: 'Floor Plans',
  gallery: 'Gallery',
  amenities: 'Amenities & Pets',
  map: 'Explore',
  more: 'About',
};

const navPages = mergedPages.filter((page) => page.visible !== false && !page.template);
const navPageByAlias = new Map(navPages.map((page) => [page.alias, page]));
const assignedAliases = new Set(
  NAV_STRUCTURE.flatMap((entry) => ('alias' in entry ? [entry.alias] : entry.aliases)),
);
// Visible pages not mapped above (e.g. newly added pages) land in "More".
const unassignedPages = navPages.filter((page) => !assignedAliases.has(page.alias));

function toNavItem(alias: string): NavItem | null {
  const page = navPageByAlias.get(alias);
  if (!page) return null;
  return { alias, label: NAV_CHILD_LABELS[alias] ?? page.name, href: page.path };
}

function normalizePath(path: string): string {
  const trimmed = path.trim().split(/[?#]/)[0].replace(/\/+$/, '');
  return (trimmed || '/').toLowerCase();
}

function buildNavEntries(
  groupLabels: Record<NavGroupKey, string>,
  includeItem: (item: NavItem) => boolean,
): NavEntry[] {
  const entries: NavEntry[] = [];
  for (const entry of NAV_STRUCTURE) {
    if ('alias' in entry) {
      const item = toNavItem(entry.alias);
      if (item && includeItem(item)) entries.push({ key: entry.alias, label: item.label, href: item.href });
      continue;
    }
    const children = entry.aliases
      .map(toNavItem)
      .concat(entry.group === 'more' ? unassignedPages.map((p) => ({ alias: p.alias, label: p.name, href: p.path })) : [])
      .filter((item): item is NavItem => !!item && includeItem(item));
    if (children.length === 0) continue;
    if (children.length === 1) {
      // A lone Floor Plans page reads as the standard link, not "All Floor Plans".
      const label = entry.group === 'floorplans' ? groupLabels.floorplans : children[0].label;
      entries.push({ key: entry.group, href: children[0].href, label });
      continue;
    }
    entries.push({
      key: entry.group,
      label: groupLabels[entry.group],
      group: entry.group,
      children,
    });
  }
  return entries;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    // No defaultField — mono_white vs mono_black is computed at runtime via luminance
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const selfGuidedTourLinkDef: SlotGroupDefinition = {
  label: 'Self Guided Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
      suggestedFields: ['custom_self_guided_tour_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Self Guided Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const visitUsLinkDef: SlotGroupDefinition = {
  label: 'Visit Us',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time. Editor overrides still win.
    url: {
      label: 'Directions URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

// ============================================================================
// Menu Nav Link
// ============================================================================

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function MenuNavLink({
  item,
  onNavigate,
}: {
  item: Pick<NavItem, 'label' | 'href'>;
  onNavigate: () => void;
}) {
  return (
    <a href={item.href} onClick={onNavigate} className="menu-nav-link">
      {item.label}
    </a>
  );
}

function NavGroupLabel({ group, label }: { group: NavGroupKey; label: string }) {
  switch (group) {
    case 'gallery':
      return <EditableToken path="header.nav_gallery_label" as="span">{label}</EditableToken>;
    case 'floorplans':
      return <EditableToken path="header.nav_floorplans_label" as="span">{label}</EditableToken>;
    case 'amenities':
      return <EditableToken path="header.nav_amenities_label" as="span">{label}</EditableToken>;
    case 'map':
      return <EditableToken path="header.nav_map_label" as="span">{label}</EditableToken>;
    case 'more':
      return <EditableToken path="header.nav_more_label" as="span">{label}</EditableToken>;
  }
}

function MenuNavGroup({
  entry,
  open,
  onToggle,
  onNavigate,
}: {
  entry: NavEntry;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const submenuId = `header-submenu-${entry.key}`;
  return (
    <div className="menu-nav-group">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={submenuId}
        className="menu-nav-link menu-nav-link--toggle"
      >
        <NavGroupLabel group={entry.group!} label={entry.label} />
        <ChevronIcon className={`menu-nav-chevron w-4 h-4 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div
        id={submenuId}
        className={`menu-nav-collapse ${open ? 'menu-nav-collapse--open' : ''}`}
      >
        <ul className="menu-nav-submenu">
          {entry.children!.map((child) => (
            <li key={child.href}>
              <a href={child.href} onClick={onNavigate} className="menu-nav-sublink">
                {child.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  const phoneToken = useContentToken('header.phone');
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;

  // Heading labels for the popped-open menu sections. Plain custom tokens
  // (no defaultField) so editors can rename them without touching property
  // data; defaults match the previously-hardcoded strings.
  const visitUsLabel = useContentToken('header.visit_us_label') || 'Visit Us';
  const callUsLabel = useContentToken('header.call_us_label') || 'Call Us';
  const officeHoursLabel = useContentToken('header.office_hours_label') || 'Office Hours';

  // Visit Us is a slot group (address + directions URL + _target) so the
  // entire block is editable as a unit and the menu's "open in new tab"
  // target stays editor-controlled.
  const visitUs = useSlotGroup('header.visit_us', visitUsLinkDef);
  const fullAddress = visitUs.address || '';
  const propertyAddressUrl = usePropertyAddressUrl();

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);

  // Read both mono variants — no defaultField in logoSlotDef so these are the
  // only source of truth when the editor hasn't set an explicit override.
  const monoWhiteLogoUrl = usePropertyField('property_mono_white_logo') as string | null;
  const monoBlackLogoUrl = usePropertyField('property_mono_black_logo') as string | null;

  // Pick the logo variant that matches the computed text color on the primary
  // background. `--vibe-color-text-on-primary` is set by the tokens generator
  // via getContrastTextColor(property_primary_color) — reading it here keeps
  // the logo flip in lockstep with the text contrast decision (no duplicated
  // luminance math, single source of truth for the threshold).
  // Defaults to mono_white (SSR-safe); updates after mount from the live CSS var.
  const [scrolledLogoVariant, setScrolledLogoVariant] = useState<'mono_white' | 'mono_black'>('mono_white');
  useEffect(() => {
    const textOnPrimary = getComputedStyle(document.documentElement)
      .getPropertyValue('--vibe-color-text-on-primary')
      .trim()
      .toLowerCase();
    // Generator emits '#1a1a1a' for dark text, '#ffffff' for light text.
    // Match dark-text shades to mono_black, otherwise mono_white.
    const isDarkText = textOnPrimary === '#1a1a1a' || textOnPrimary === '#000' || textOnPrimary === '#000000';
    setScrolledLogoVariant(isDarkText ? 'mono_black' : 'mono_white');
  }, []);

  // If the editor has set an explicit logo, use it in both states (no switching).
  // Otherwise: transparent state = always mono_white (hero bg is typically dark),
  //            scrolled state   = luminance-computed variant for primary bg.
  const customLogoUrl: string | null = logoRaw.image || null;
  const transparentLogoUrl = customLogoUrl ?? monoWhiteLogoUrl;
  const scrolledComputedLogoUrl = customLogoUrl ?? (scrolledLogoVariant === 'mono_white' ? monoWhiteLogoUrl : monoBlackLogoUrl);
  // Dual crossfade only needed when the two states would show different images.
  const useDualLogos = !customLogoUrl && !!transparentLogoUrl && !!scrolledComputedLogoUrl && transparentLogoUrl !== scrolledComputedLogoUrl;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const selfGuidedTourLink = useSlotGroup('header.self_guided_tour_link', selfGuidedTourLinkDef);
  const hasSelfGuidedTour = isUsableUrl(selfGuidedTourLink.url);

  const officeHoursRaw = useCollectionBinding<OfficeHourEntry>('header.office_hours', {
    collection: 'office_hours',
  });
  const officeHours = withClosedDays(officeHoursRaw).sort((a, b) => dayOrderIndex(a.day) - dayOrderIndex(b.day));

  // Hide /virtual-tour from nav unless the property has at least one tour
  // with a usable URL (matches VirtualTourEmbed's display rule).
  const virtualTours = useCollectionBinding<VirtualTourEntry>('header.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));

  // Hide /interactive-map from nav unless the property has an Engrain
  // sightmap ID or URL (the same fields the engrain-sitemap block reads).
  const engrainSightmapFields = [
    usePropertyField('custom_engrain_sightmap_url'),
    usePropertyField('custom_engrain_sitemap_url'),
    usePropertyField('custom_engrain_sightmap_id'),
    usePropertyField('custom_engrain_sitemap_id'),
  ] as Array<string | null | undefined>;
  const hasInteractiveMap = engrainSightmapFields.some(isUsableUrl);

  // Hide floor plan type pages (Studios, 1–3 Bedrooms, Townhomes) unless the
  // property has at least one plan of that type (matches FloorplanShowcase).
  const floorplans = useCollectionBinding<Floorplan>('header.floorplans', {
    collection: 'floorplans',
  });
  // `pagePaths` lists the pages this site actually ships, so it reflects pages
  // hidden or deleted in page settings, which the static pages.json merge above
  // can't see. When absent, fall back to the static merge only.
  const { pagePaths } = useVibesites();
  const shippedPaths = pagePaths?.length ? new Set(pagePaths.map(normalizePath)) : null;
  const hiddenFloorplanTypeAliases = new Set(
    Object.entries(FLOORPLAN_TYPE_PAGES)
      .filter(([, filter]) => !(floorplans || []).some((plan) => matchesFloorplanType(plan, filter)))
      .map(([alias]) => alias),
  );

  const navGroupLabels: Record<NavGroupKey, string> = {
    floorplans: useContentToken('header.nav_floorplans_label') || NAV_GROUP_DEFAULT_LABELS.floorplans,
    gallery: useContentToken('header.nav_gallery_label') || NAV_GROUP_DEFAULT_LABELS.gallery,
    amenities: useContentToken('header.nav_amenities_label') || NAV_GROUP_DEFAULT_LABELS.amenities,
    map: useContentToken('header.nav_map_label') || NAV_GROUP_DEFAULT_LABELS.map,
    more: useContentToken('header.nav_more_label') || NAV_GROUP_DEFAULT_LABELS.more,
  };
  const navEntries = buildNavEntries(
    navGroupLabels,
    (item) =>
      (!shippedPaths || shippedPaths.has(normalizePath(item.href))) &&
      !(item.alias === 'virtual-tour' && !hasVirtualTours) &&
      !(item.alias === 'interactive-map' && !hasInteractiveMap) &&
      !hiddenFloorplanTypeAliases.has(item.alias),
  );
  const [openNavGroup, setOpenNavGroup] = useState<string | null>(null);
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // URL resolution order:
  //   1. The visit_us slot group's `url` field (editor override).
  //   2. usePropertyAddressUrl() — falls through Google place URL → business URL
  //      → full address → lat/lon at the runtime layer.
  //   3. A constructed Google Maps directions URL from the address (legacy fallback).
  //   4. '#' as a last resort so the anchor stays valid.
  const directionsUrl = isUsableUrl(visitUs.url)
    ? visitUs.url!
    : (propertyAddressUrl
        || (fullAddress
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(String(fullAddress))}`
            : '#'));
  const directionsTarget = visitUs._target || '_blank';

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenNavGroup(null);
  };

  return (
    <>
      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full"
        style={{ top: 'var(--offer-banner-height, 0px)', transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {/* ================================================================ */}
        {/* Utility Bar                                                      */}
        {/* ================================================================ */}
        <div className="header-utility-bar">
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-9">
              {/* Left: Address + Phone (desktop) */}
              <div className="hidden md:flex items-center divide-x divide-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_20%,transparent)]">
                <EditableSlotGroup groupPath="header.visit_us" definition={visitUsLinkDef}>
                  <a
                    href={directionsUrl}
                    target={directionsTarget}
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 pr-4 text-xs tracking-wide text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{fullAddress}</span>
                  </a>
                </EditableSlotGroup>

                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref flex items-center gap-1.5 pl-4 text-xs tracking-wide text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken path="header.phone" as="span">
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                )}
              </div>

              {/* Right: Utility CTA links (desktop only — hidden on tablet) */}
              <div className="hidden lg:flex items-center gap-2 ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="header-pill-btn header-pill-btn--sm"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="header-pill-btn header-pill-btn--sm"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    rel={
                      (scheduleTourLink._target || '_self') === '_blank'
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    className="header-pill-btn header-pill-btn--sm"
                  >
                    {scheduleTourLink.label || 'Schedule a Tour'}
                  </a>
                </EditableSlotGroup>

                {hasSelfGuidedTour && (
                  <EditableSlotGroup groupPath="header.self_guided_tour_link" definition={selfGuidedTourLinkDef}>
                    <a
                      href={selfGuidedTourLink.url!}
                      target={selfGuidedTourLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                    >
                      {selfGuidedTourLink.label || 'Self Guided Tour'}
                    </a>
                  </EditableSlotGroup>
                )}
              </div>

              {/* Mobile utility row: phone + directions */}
              <div className="md:hidden flex items-center justify-between w-full text-xs">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref flex items-center gap-1.5 text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-opacity"
                  >
                    <PhoneIcon className="w-3.5 h-3.5" />
                    <span>{formatPhone(phone)}</span>
                  </a>
                )}
                <a
                  href={directionsUrl}
                  target={directionsTarget}
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-opacity"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Main Navigation Bar (transparent)                                */}
        {/* ================================================================ */}
        <div className={`main-nav-bar transition-all duration-500 ease-out ${scrolled ? 'main-nav-bar--scrolled' : ''} ${menuOpen ? 'main-nav-bar--menu-open' : ''}`}>
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {useDualLogos ? (
                    // Two overlapping logos that crossfade on the same 500ms timing
                    // as the header background transition.
                    // "active bg" = scrolled or menu open (header has solid primary color).
                    <div className="relative h-10 lg:h-12 flex items-center">
                      {/* Transparent-state logo (mono_white) — fades out when bg goes solid */}
                      <VibesitesImage
                        url={transparentLogoUrl!}
                        alt={logoRaw.alt || 'Community Logo'}
                        sizes="160px"
                        className={`absolute h-10 lg:h-12 w-auto object-contain transition-opacity duration-500 ease-out ${
                          scrolled || menuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                      />
                      {/* Scrolled-state logo (luminance-computed) — fades in when bg goes solid */}
                      <VibesitesImage
                        url={scrolledComputedLogoUrl!}
                        alt={logoRaw.alt || 'Community Logo'}
                        sizes="160px"
                        className={`h-10 lg:h-12 w-auto object-contain transition-opacity duration-500 ease-out ${
                          scrolled || menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                      />
                    </div>
                  ) : (transparentLogoUrl || scrolledComputedLogoUrl) ? (
                    <VibesitesImage
                      url={(transparentLogoUrl || scrolledComputedLogoUrl)!}
                      alt={logoRaw.alt || 'Community Logo'}
                      sizes="160px"
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className={`font-heading text-xl lg:text-2xl font-bold transition-colors duration-500 ${
                      scrolled || menuOpen ? 'header-logo-text-active' : 'text-white'
                    }`}>
                      The Preferred
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Hamburger — always visible, all navigation lives in the menu */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="relative w-10 h-10 flex items-center justify-center"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Hamburger Menu Overlay — all navigation lives here               */}
        {/* ================================================================ */}
        <div
          className={`fixed inset-x-0 bottom-0 mobile-menu-bg overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))` }}
        >
          <div className="flex flex-col md:flex-row md:items-start">
            {/* Left column: navigation + CTAs */}
            <div className="flex-1 min-w-0 md:border-r md:border-[color-mix(in_srgb,var(--vibe-color-primary)_12%,transparent)]">
              <nav role="navigation" aria-label="Primary navigation">
                {navEntries.map((entry) =>
                  entry.children ? (
                    <MenuNavGroup
                      key={entry.key}
                      entry={entry}
                      open={openNavGroup === entry.key}
                      onToggle={() => setOpenNavGroup(openNavGroup === entry.key ? null : entry.key)}
                      onNavigate={closeMobileMenu}
                    />
                  ) : (
                    <MenuNavLink
                      key={entry.key}
                      item={{ label: entry.label, href: entry.href! }}
                      onNavigate={closeMobileMenu}
                    />
                  ),
                )}
              </nav>

              <div className="p-5 space-y-3">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    rel={
                      (scheduleTourLink._target || '_self') === '_blank'
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    onClick={closeMobileMenu}
                    className="menu-cta-btn menu-cta-btn--solid group/cta gap-2 w-full py-3.5 text-sm"
                  >
                    <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
                    <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>

                {hasSelfGuidedTour && (
                  <EditableSlotGroup groupPath="header.self_guided_tour_link" definition={selfGuidedTourLinkDef}>
                    <a
                      href={selfGuidedTourLink.url!}
                      target={selfGuidedTourLink._target || '_blank'}
                      rel="noopener noreferrer"
                      onClick={closeMobileMenu}
                      className="menu-cta-btn menu-cta-btn--outline group/cta gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase"
                    >
                      <span className="relative z-10">{selfGuidedTourLink.label || 'Self Guided Tour'}</span>
                      <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                      </svg>
                    </a>
                  </EditableSlotGroup>
                )}
              </div>

              <div className="px-5 pb-5">
                <div className="grid grid-cols-2 gap-2">
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="menu-cta-btn menu-cta-btn--outline w-full py-2.5 text-xs"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="menu-cta-btn menu-cta-btn--outline w-full py-2.5 text-xs"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>

            {/* Right column: contact info block, aligned right */}
            <aside
              className="md:w-80 lg:w-96 md:flex-shrink-0 px-5 py-6 border-t md:border-t-0 border-[color-mix(in_srgb,var(--vibe-color-primary)_12%,transparent)] text-right"
              aria-label="Contact information"
            >
              {fullAddress && (
                <div className="mb-5">
                  <EditableToken path="header.visit_us_label" as="h2" className="menu-contact-label">
                    {visitUsLabel}
                  </EditableToken>
                  <EditableSlotGroup groupPath="header.visit_us" definition={visitUsLinkDef}>
                    <a
                      href={directionsUrl}
                      target={directionsTarget}
                      rel="noopener noreferrer"
                      className="menu-contact-value hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      {fullAddress}
                    </a>
                  </EditableSlotGroup>
                </div>
              )}

              {phone && (
                <div className="mb-5">
                  <EditableToken path="header.call_us_label" as="h2" className="menu-contact-label">
                    {callUsLabel}
                  </EditableToken>
                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref menu-contact-value hover:text-[var(--vibe-color-primary)] transition-colors"
                  >
                    <EditableToken path="header.phone" as="span">
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>
              )}

              {officeHours.length > 0 && (
                <div>
                  <EditableToken path="header.office_hours_label" as="h2" className="menu-contact-label">
                    {officeHoursLabel}
                  </EditableToken>
                  <dl className="menu-hours-grid">
                    {officeHours.map((h, i) => (
                      <React.Fragment key={i}>
                        <dt className="menu-hours-day">{formatDay(h.day)}</dt>
                        <dd className="menu-hours-time">{formatHoursValue(h.open_time, h.close_time)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </aside>
          </div>
        </div>
      </header>

      <style>{`
        /* ============================================================ */
        /* Utility bar                                                   */
        /* ============================================================ */
        .header-utility-bar {
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
          color: var(--vibe-color-text-on-secondary);
        }

        /* ============================================================ */
        /* Main nav bar — transparent by default, solid on scroll        */
        /* ============================================================ */
        .main-nav-bar {
          background: transparent;
        }
        .main-nav-bar--scrolled {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }

        /* When mobile menu is open, give the nav bar a solid background
           so the hero image doesn't bleed through behind the logo/close row */
        .main-nav-bar--menu-open {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }

        /* ============================================================ */
        /* Fallback text logo when no image is available                 */
        /* ============================================================ */
        .header-logo-text-active {
          color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Hamburger lines — white on transparent, textOnPrimary on solid */
        /* ============================================================ */
        .hamburger-line {
          background-color: white;
          transition: background-color 0.5s ease;
        }
        .main-nav-bar--scrolled .hamburger-line,
        .main-nav-bar--menu-open .hamburger-line {
          background-color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Hamburger menu background                                     */
        /* ============================================================ */
        .mobile-menu-bg {
          background: white;
        }

        /* ============================================================ */
        /* Hamburger menu primary navigation (grouped)                   */
        /* ============================================================ */
        .menu-nav-link {
          display: block;
          width: 100%;
          padding: 0.875rem 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          text-align: left;
          color: var(--vibe-color-primary);
          text-decoration: none;
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          transition: color 0.2s ease, background-color 0.2s ease;
        }
        .menu-nav-link--toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
        }
        .menu-nav-link:hover,
        .menu-nav-link[aria-expanded="true"] {
          background: color-mix(in srgb, var(--vibe-color-primary) 5%, transparent);
        }
        .menu-nav-chevron {
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .menu-nav-collapse {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .menu-nav-collapse--open {
          grid-template-rows: 1fr;
        }
        .menu-nav-submenu {
          min-height: 0;
          overflow: hidden;
          background: color-mix(in srgb, var(--vibe-color-primary) 4%, white);
          opacity: 0;
          transform: translateY(-0.375rem);
          visibility: hidden;
          /* Stay visible until the collapse finishes, then hide from focus/AT. */
          transition:
            opacity 0.25s ease,
            transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
            visibility 0s linear 0.4s;
        }
        .menu-nav-collapse--open .menu-nav-submenu {
          opacity: 1;
          transform: translateY(0);
          visibility: visible;
          transition:
            opacity 0.35s ease 0.05s,
            transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
            visibility 0s;
        }
        /* Mouse users: groups open on hover. The short delay stops groups
           flickering open as the cursor sweeps past them. */
        @media (hover: hover) and (pointer: fine) {
          .menu-nav-group:hover .menu-nav-collapse {
            grid-template-rows: 1fr;
            transition-delay: 0.12s;
          }
          .menu-nav-group:hover .menu-nav-submenu {
            opacity: 1;
            transform: translateY(0);
            visibility: visible;
            transition:
              opacity 0.35s ease 0.17s,
              transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.12s,
              visibility 0s linear 0.12s;
          }
          .menu-nav-group:hover .menu-nav-chevron {
            transform: rotate(180deg);
            transition-delay: 0.12s;
          }
          .menu-nav-group:hover > .menu-nav-link {
            background: color-mix(in srgb, var(--vibe-color-primary) 5%, transparent);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .menu-nav-chevron,
          .menu-nav-collapse,
          .menu-nav-submenu,
          .menu-nav-collapse--open .menu-nav-submenu,
          .menu-nav-group:hover .menu-nav-chevron,
          .menu-nav-group:hover .menu-nav-collapse,
          .menu-nav-group:hover .menu-nav-submenu {
            transition: none;
          }
        }
        .menu-nav-sublink {
          display: block;
          padding: 0.7rem 1.5rem 0.7rem 2.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 500;
          letter-spacing: 0.03em;
          color: color-mix(in srgb, var(--vibe-color-primary) 80%, white);
          text-decoration: none;
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
          transition: color 0.2s ease;
        }
        .menu-nav-sublink:hover {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* ============================================================ */
        /* Hamburger menu contact info block                             */
        /* ============================================================ */
        .menu-contact-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: color-mix(in srgb, var(--vibe-color-primary) 65%, white);
          margin: 0 0 0.4rem;
        }
        .menu-contact-value {
          display: block;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: color-mix(in srgb, var(--vibe-color-primary) 85%, white);
          text-decoration: none;
        }
        .menu-hours-grid {
          display: grid;
          grid-template-columns: auto auto;
          justify-content: end;
          column-gap: 1rem;
          row-gap: 0.2rem;
          margin: 0;
        }
        .menu-hours-day {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          font-weight: 600;
          color: var(--vibe-color-primary);
          text-align: left;
        }
        .menu-hours-time {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          color: color-mix(in srgb, var(--vibe-color-primary) 75%, white);
          text-align: right;
          margin: 0;
        }

        /* ============================================================ */
        /* CTA pill buttons (white pill, matches the CTA card buttons)   */
        /* ============================================================ */
        .header-pill-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          letter-spacing: 0.04em;
          line-height: 1.3;
          text-align: center;
          white-space: nowrap;
          color: var(--vibe-color-text);
          background: white;
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .header-pill-btn--sm {
          padding: 0.25rem 0.875rem;
          font-size: 0.6875rem;
        }
        .header-pill-btn:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .header-pill-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }

        /* ============================================================ */
        /* Hamburger menu CTAs (on white): solid blue + blue outline     */
        /* ============================================================ */
        .menu-cta-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          letter-spacing: 0.04em;
          line-height: 1.3;
          text-align: center;
          white-space: nowrap;
          border-radius: 9999px;
          border: 2px solid var(--vibe-color-primary);
          transition: all 0.3s ease;
        }
        .menu-cta-btn--solid {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .menu-cta-btn--solid:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 85%, black);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 85%, black);
        }
        .menu-cta-btn--outline {
          background: white;
          color: var(--vibe-color-primary);
        }
        .menu-cta-btn--outline:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .menu-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .menu-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      // Empty so a missing call-tracking number falls through to property_phone
      // in the component (placeholder is applied there as the final fallback).
      defaultValue: '',
    },
    'header.visit_us_label': {
      label: 'Visit Us Heading',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'header.call_us_label': {
      label: 'Call Us Heading',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'header.office_hours_label': {
      label: 'Office Hours Heading',
      type: 'text',
      defaultValue: 'Office Hours',
    },
    'header.nav_floorplans_label': {
      label: 'Menu Group: Floor Plans',
      type: 'text',
      defaultValue: 'Floor Plans',
    },
    'header.nav_gallery_label': {
      label: 'Menu Group: Gallery',
      type: 'text',
      defaultValue: 'Gallery',
    },
    'header.nav_amenities_label': {
      label: 'Menu Group: Amenities',
      type: 'text',
      defaultValue: 'Amenities & Pets',
    },
    'header.nav_map_label': {
      label: 'Menu Group: Explore',
      type: 'text',
      defaultValue: 'Explore',
    },
    'header.nav_more_label': {
      label: 'Menu Group: About',
      type: 'text',
      defaultValue: 'About',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        // No defaultField — mono_white vs mono_black is computed at runtime via luminance
        image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.visit_us': {
      label: 'Visit Us',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at render
        // time. Editor overrides still win.
        url: {
          label: 'Directions URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/apply-now',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.self_guided_tour_link': {
      label: 'Self Guided Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'custom_self_guided_tour_url',
          suggestedFields: ['custom_self_guided_tour_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Self Guided Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
    'header.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
    'header.floorplans': {
      label: 'Floor Plans (visibility check)',
      collection: 'floorplans',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
